// Policy Delivery Summary — the one-page document an agent hands a
// client right after a policy is issued. Doubles as a retention /
// professionalism touchpoint and cuts down "what's my policy number
// again?" calls a year from now.
//
// Form on the left auto-populates from the client record (name, address,
// phone, email, carrier, policy, DOB) and the agent's settings (name,
// license, phone, email, agency). Operator fills the gaps (brand,
// product type, death benefit, monthly premium, draft date, payment
// method, primary + contingent beneficiaries, the "why" note).
//
// Live preview on the right re-renders on every keystroke so the
// agent sees exactly what the client will see before they Print or
// Email.
//
// Persistence: form values are stashed under
// localStorage.policyDelivery:<clientId> so a half-filled summary
// survives a tab switch. Storage is per-browser, NOT mirrored to
// Supabase — the document itself is the artifact, not the form state.

import React from 'react';
import { supabase } from '../lib/supabase';

const BRANDS = [
  { id: 'for_the_people',  label: 'For The People',  color: '#0F766E' },
  { id: 'legacy_wealth',   label: 'Legacy Wealth',   color: '#9333EA' },
  { id: 'shark_financial', label: 'Shark Financial', color: '#0EA5E9' },
  { id: 'custom',          label: 'Custom (other)',  color: '#0F172A' },
];

const PRODUCT_TYPES = [
  'Term Life',
  'Whole Life',
  'Universal Life',
  'Indexed Universal Life (IUL)',
  'Final Expense',
  'Mortgage Protection',
  'Other',
];

const PAYMENT_METHODS = ['Bank draft (ACH)', 'Credit card', 'Direct bill (check)', 'Payroll deduction'];

const STATUSES = ['Active', 'Pending issue', 'In underwriting', 'Approved (awaiting first draft)'];

function defaultState(client, settings) {
  // Guard against null — default params only fire for undefined, and this
  // modal is mounted with client={deliveryClient} where deliveryClient is
  // null on initial render. Reading client.policy on null is BUG-001.
  const c = client || {};
  const s = settings || {};
  return {
    // Brand
    brand_id:        'for_the_people',
    brand_custom:    '',
    // Policy
    policy_number:   c.policy || '',
    carrier:         c.carrier || '',
    carrier_portal:  '',
    portal_login:    '',
    product_type:    PRODUCT_TYPES[0],
    issue_date:      c.issue_date || '',
    effective_date:  c.issue_date || '',
    status:          STATUSES[0],
    // Coverage
    death_benefit:   '',
    monthly_premium: c.draft_amount ? String(c.draft_amount) : '',
    draft_date:      c.first_draft_date || '',
    payment_method:  PAYMENT_METHODS[0],
    beneficiary_primary_name:        '',
    beneficiary_primary_relation:    '',
    beneficiary_contingent_name:     '',
    beneficiary_contingent_relation: '',
    // Why note
    why_note: '',
    // Agent (defaults from settings; agent can override)
    agent_name:      s.ownerName  || '',
    agent_license:   s.agentLicense || '',
    agent_phone:     s.ownerPhone || '',
    agent_email:     s.ownerEmail || '',
    service_line:    "Call or text me anytime. For claims, I'll walk you through every step.",
  };
}

export default function PolicyDeliveryModal({ client, settings = {}, open, onClose, showToast = () => {}, onDelivered = () => {} }) {
  const storageKey = `policyDelivery:${client?.id || 'new'}`;
  const [form, setForm] = React.useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...defaultState(client, settings), ...parsed };
      }
    } catch { /* ignore */ }
    return defaultState(client, settings);
  });
  // Re-seed when the modal opens for a different client.
  React.useEffect(() => {
    if (!open || !client) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        setForm({ ...defaultState(client, settings), ...parsed });
        return;
      }
    } catch { /* ignore */ }
    setForm(defaultState(client, settings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id, open]);
  // Persist on every change so a tab switch doesn't lose progress.
  React.useEffect(() => {
    if (!open) return;
    try { localStorage.setItem(storageKey, JSON.stringify(form)); } catch { /* ignore */ }
  }, [form, open, storageKey]);

  // Hooks must run in the same order every render — declare all of them
  // before the early-return guard below.
  const [markingSent, setMarkingSent] = React.useState(false);

  if (!open || !client) return null;

  const set = (k) => (e) => setForm(s => ({ ...s, [k]: e.target.value }));
  const brand = BRANDS.find(b => b.id === form.brand_id) || BRANDS[0];
  const brandName = form.brand_id === 'custom' ? (form.brand_custom || 'Custom Brand') : brand.label;

  const fmtMoney = (v) => {
    const n = Number(String(v).replace(/[^\d.]/g, ''));
    if (!Number.isFinite(n) || n === 0) return '';
    return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  };
  const fmtDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d + (d.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return d; }
  };

  const copyHtml = async () => {
    try {
      const html = document.getElementById('pd-preview-root')?.outerHTML || '';
      await navigator.clipboard.writeText(html);
      showToast('Delivery summary HTML copied — paste into your email client.');
    } catch {
      showToast('Copy failed — try Print instead.', 'error');
    }
  };

  const doPrint = () => {
    // The print stylesheet (defined below in <style>) hides the form +
    // chrome and prints the preview pane only.
    window.print();
  };

  const isAlreadyDelivered = !!client.delivery_sent_at;
  const markDelivered = async (method = 'manual') => {
    if (!client?.id) return;
    setMarkingSent(true);
    try {
      const { error } = await supabase
        .from('clients')
        .update({ delivery_sent_at: new Date().toISOString(), delivery_method: method })
        .eq('id', client.id);
      if (error) {
        showToast(`Couldn't mark delivered: ${error.message}`, 'error');
        return;
      }
      showToast(`Marked delivered for ${client.name}.`);
      onDelivered(client.id, method);
    } finally {
      setMarkingSent(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-stretch justify-center p-0 md:p-4 print:p-0 print:bg-white" onClick={onClose}>
      <div
        id="pd-modal"
        className="bg-slate-50 w-full max-w-6xl shadow-2xl flex flex-col md:rounded-xl overflow-hidden print:shadow-none print:rounded-none print:max-w-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Print-only stylesheet */}
        <style>{`
          @media print {
            body * { visibility: hidden; }
            #pd-preview-wrap, #pd-preview-wrap * { visibility: visible; }
            #pd-preview-wrap { position: absolute; left: 0; top: 0; width: 100%; padding: 32px; }
            .pd-no-print { display: none !important; }
            #pd-preview-root { box-shadow: none !important; border: none !important; }
          }
        `}</style>

        {/* Header */}
        <div className="bg-slate-900 text-white px-5 py-3 flex items-center justify-between gap-3 pd-no-print">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Policy Delivery Summary</div>
            <div className="font-bold">{client.name}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copyHtml} className="bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded">Copy HTML</button>
            <button onClick={doPrint} className="bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded">Print / Save PDF</button>
            {/* Mark delivered — the explicit "we sent this to the client"
                stamp. Copy / Print are prep actions; only this clears
                the pending-deliveries alert on Home. Already-delivered
                clients show the stamped timestamp instead of the button. */}
            {isAlreadyDelivered ? (
              <span
                title={`Marked delivered ${new Date(client.delivery_sent_at).toLocaleString()}${client.delivery_method ? ' (' + client.delivery_method + ')' : ''}`}
                className="bg-emerald-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded inline-flex items-center gap-1"
              >
                ✓ Delivered
              </span>
            ) : (
              <button
                onClick={() => markDelivered('manual')}
                disabled={markingSent}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded"
                title="Mark this delivery as sent. Clears the pending-deliveries alert on Home."
              >
                {markingSent ? 'Saving…' : '✓ Mark delivered'}
              </button>
            )}
            <button onClick={onClose} className="text-slate-300 hover:text-white text-xl leading-none px-2">×</button>
          </div>
        </div>

        {/* Body — form left, preview right */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-0 overflow-hidden">
          {/* Form pane */}
          <div className="overflow-y-auto p-5 space-y-4 bg-white border-r border-slate-200 pd-no-print">
            <Section title="Brand">
              <Field label="Brand">
                <select value={form.brand_id} onChange={set('brand_id')} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm">
                  {BRANDS.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
                </select>
              </Field>
              {form.brand_id === 'custom' && (
                <Field label="Custom brand name">
                  <input value={form.brand_custom} onChange={set('brand_custom')} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm" placeholder="e.g. Coastal Insurance Group"/>
                </Field>
              )}
            </Section>

            <Section title="Policy">
              <Field label="Policy number">
                <input value={form.policy_number} onChange={set('policy_number')} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm font-mono"/>
              </Field>
              <Field label="Carrier">
                <input value={form.carrier} onChange={set('carrier')} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"/>
              </Field>
              <Field label="Carrier portal URL">
                <input value={form.carrier_portal} onChange={set('carrier_portal')} placeholder="https://…" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm font-mono"/>
              </Field>
              <Field label="Portal login instructions">
                <input value={form.portal_login} onChange={set('portal_login')} placeholder='e.g. "Username is your email; click Forgot Password to set"' className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"/>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Product type">
                  <select value={form.product_type} onChange={set('product_type')} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm">
                    {PRODUCT_TYPES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <select value={form.status} onChange={set('status')} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm">
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Issue date">
                  <input type="date" value={form.issue_date} onChange={set('issue_date')} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"/>
                </Field>
                <Field label="Effective date">
                  <input type="date" value={form.effective_date} onChange={set('effective_date')} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"/>
                </Field>
              </div>
            </Section>

            <Section title="Coverage">
              <div className="grid grid-cols-2 gap-2">
                <Field label="Death benefit ($)">
                  <input value={form.death_benefit} onChange={set('death_benefit')} placeholder="250000" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm font-mono"/>
                </Field>
                <Field label="Monthly premium ($)">
                  <input value={form.monthly_premium} onChange={set('monthly_premium')} placeholder="48.50" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm font-mono"/>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Draft date">
                  <input value={form.draft_date} onChange={set('draft_date')} placeholder="15th of each month" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"/>
                </Field>
                <Field label="Payment method">
                  <select value={form.payment_method} onChange={set('payment_method')} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm">
                    {PAYMENT_METHODS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Primary beneficiary">
                  <input value={form.beneficiary_primary_name} onChange={set('beneficiary_primary_name')} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"/>
                </Field>
                <Field label="Relationship">
                  <input value={form.beneficiary_primary_relation} onChange={set('beneficiary_primary_relation')} placeholder="Spouse / Child / Estate" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"/>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Contingent beneficiary">
                  <input value={form.beneficiary_contingent_name} onChange={set('beneficiary_contingent_name')} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"/>
                </Field>
                <Field label="Relationship">
                  <input value={form.beneficiary_contingent_relation} onChange={set('beneficiary_contingent_relation')} placeholder="Optional" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"/>
                </Field>
              </div>
            </Section>

            <Section title='The "why"'>
              <Field label="Personalized note (2-4 sentences — what this policy is doing for them)">
                <textarea
                  value={form.why_note}
                  onChange={set('why_note')}
                  rows={4}
                  placeholder='Example: "Chosen to protect your mortgage and replace 10 years of your income for Sarah and the kids. The 20-year term matches the years left on your house, so the coverage is in force exactly when your family needs it most."'
                  className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm leading-relaxed"
                />
              </Field>
            </Section>

            <Section title="Agent (you)">
              <div className="grid grid-cols-2 gap-2">
                <Field label="Agent name">
                  <input value={form.agent_name} onChange={set('agent_name')} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"/>
                </Field>
                <Field label="License #">
                  <input value={form.agent_license} onChange={set('agent_license')} placeholder="State + number" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm font-mono"/>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Phone">
                  <input value={form.agent_phone} onChange={set('agent_phone')} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"/>
                </Field>
                <Field label="Email">
                  <input value={form.agent_email} onChange={set('agent_email')} type="email" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"/>
                </Field>
              </div>
              <Field label="Service / claims line">
                <input value={form.service_line} onChange={set('service_line')} className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"/>
              </Field>
            </Section>

            <div className="text-xs text-slate-500 leading-relaxed pt-2 border-t border-slate-100">
              Form values are saved per-client in your browser. Print to save as PDF (Chrome / Safari → Save as PDF in the print dialog) or Copy HTML to paste into your email.
            </div>
          </div>

          {/* Preview pane */}
          <div id="pd-preview-wrap" className="overflow-y-auto bg-slate-100 print:bg-white p-4 md:p-6 print:p-0">
            <div
              id="pd-preview-root"
              className="bg-white shadow-md border border-slate-200 max-w-[8.5in] mx-auto p-8 md:p-10 text-slate-900"
              style={{ minHeight: '11in', fontFamily: '-apple-system, "Segoe UI", system-ui, sans-serif' }}
            >
              {/* Brand bar */}
              <div
                className="flex items-center justify-between gap-3 pb-4 border-b-2 mb-6"
                style={{ borderColor: brand.color }}
              >
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: brand.color }}>
                    {brandName}
                  </div>
                  <h1 className="text-2xl font-black mt-1 text-slate-900 leading-tight">Your Policy at a Glance</h1>
                </div>
                <div className="text-xs text-slate-500 text-right leading-tight">
                  Delivered<br/>
                  <span className="font-semibold text-slate-700">{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
              </div>

              {/* Client */}
              <div className="mb-6">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">For</div>
                <div className="text-xl font-bold text-slate-900 mt-0.5">{client.name}</div>
                {client.address && <div className="text-sm text-slate-600 mt-0.5">{client.address}</div>}
                <div className="text-sm text-slate-600 mt-0.5">
                  {client.phone && <span>{client.phone}</span>}
                  {client.phone && client.email && <span> · </span>}
                  {client.email && <span>{client.email}</span>}
                </div>
              </div>

              {/* Policy details */}
              <PreviewSection title="Policy details">
                <PreviewRow label="Policy number" value={form.policy_number || '—'} mono/>
                <PreviewRow label="Carrier" value={form.carrier || '—'}/>
                <PreviewRow label="Product type" value={form.product_type}/>
                <PreviewRow label="Issue / effective date" value={[fmtDate(form.issue_date), fmtDate(form.effective_date)].filter(Boolean).join(' / ') || '—'}/>
                <PreviewRow label="Policy status" value={form.status}/>
                {form.carrier_portal && (
                  <PreviewRow label="Carrier portal" value={form.carrier_portal} mono/>
                )}
                {form.portal_login && (
                  <PreviewRow label="Login instructions" value={form.portal_login}/>
                )}
              </PreviewSection>

              {/* Coverage */}
              <PreviewSection title="Your coverage">
                <PreviewRow label="Death benefit (face amount)" value={fmtMoney(form.death_benefit) || '—'} bold/>
                <PreviewRow label="Monthly premium" value={fmtMoney(form.monthly_premium) || '—'}/>
                <PreviewRow label="Draft date" value={form.draft_date || '—'}/>
                <PreviewRow label="Payment method" value={form.payment_method}/>
                <PreviewRow
                  label="Primary beneficiary"
                  value={form.beneficiary_primary_name
                    ? `${form.beneficiary_primary_name}${form.beneficiary_primary_relation ? ` (${form.beneficiary_primary_relation})` : ''}`
                    : '—'}
                />
                {form.beneficiary_contingent_name && (
                  <PreviewRow
                    label="Contingent beneficiary"
                    value={`${form.beneficiary_contingent_name}${form.beneficiary_contingent_relation ? ` (${form.beneficiary_contingent_relation})` : ''}`}
                  />
                )}
              </PreviewSection>

              {/* Why */}
              {form.why_note && (
                <PreviewSection title="Why this policy fits you" accent={brand.color}>
                  <p className="text-sm leading-relaxed text-slate-700 italic">{form.why_note}</p>
                </PreviewSection>
              )}

              {/* Agent */}
              <PreviewSection title="Your agent">
                <div className="text-sm text-slate-700 leading-relaxed">
                  <div className="font-bold text-slate-900 text-base">{form.agent_name || '—'}</div>
                  {form.agent_license && <div className="text-xs text-slate-500 mt-0.5">License: <span className="font-mono">{form.agent_license}</span></div>}
                  <div className="mt-1">
                    {form.agent_phone && <span>{form.agent_phone}</span>}
                    {form.agent_phone && form.agent_email && <span className="text-slate-400"> · </span>}
                    {form.agent_email && <span>{form.agent_email}</span>}
                  </div>
                  {form.service_line && <div className="mt-2 text-xs text-slate-600 italic">{form.service_line}</div>}
                </div>
              </PreviewSection>

              {/* Footer */}
              <div className="text-[10px] text-slate-400 leading-relaxed mt-8 pt-4 border-t border-slate-200">
                This summary is provided as a courtesy and reflects information at the time of delivery. The carrier's policy contract is the binding document. For changes, claims, or questions, contact your agent above or the carrier directly.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Small layout helpers ─────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-600 block mb-1">{label}</span>
      {children}
    </label>
  );
}

function PreviewSection({ title, children, accent }) {
  return (
    <div className="mb-5" style={accent ? { borderLeft: `3px solid ${accent}`, paddingLeft: '12px' } : undefined}>
      <h2 className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">{title}</h2>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function PreviewRow({ label, value, mono, bold }) {
  return (
    <div className="flex items-start justify-between gap-3 py-0.5">
      <div className="text-sm text-slate-500 shrink-0">{label}</div>
      <div className={`text-sm text-right text-slate-900 ${mono ? 'font-mono' : ''} ${bold ? 'font-bold text-base' : ''}`}>
        {value || '—'}
      </div>
    </div>
  );
}
